// Layouts
import React, { useContext, useEffect, useCallback, useMemo, useState } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Image, Modal, Platform, Pressable, StyleSheet, View } from 'react-native';
import { MuseumApp } from '../layouts/museum/MuseumAppRoutes';
import { PlaceApp } from '../layouts/place/PlaceAppRoutes';
import { PlaceText } from '@/layouts/genericPages/components/PlaceText';
import Toast from 'react-native-toast-message';
import { FindifyToastConfig } from '@/layouts/genericPages/components/FindifyToast';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { InitialPage } from "./InitialPage";
import { InitialPagePlacesList } from "./InitialPagePlaceList";
import { ScanQrTab } from "@/layouts/genericPages/ScanQr";
import { EventsApp } from '@/layouts/events/EventsAppRoutes';
import { FindifyDataContext, FindifyDataContextType } from '@/contexts/FindifyDataContext';
import Config from 'react-native-config';
import { MobileWorkspacesApi } from "../findifyv2-api-ts/mobile/workspaces/WorkspacesApi";
import { FindifyLoadingScreen } from '../layouts/genericPages/components/FindifyLoadingScreen';

const Stack = createNativeStackNavigator();

const PwaInstallPrompt = () => {
  const findifyDataContext = useContext(FindifyDataContext);
  const [installEvent, setInstallEvent] = useState<any>(null);
  const [visible, setVisible] = useState(false);
  const colors = { primary: '#b74433', secondary: '#601b2d', background: '#f4efea', text: '#3e3836' };
  const museumLogo = findifyDataContext?.workspace?.app_settings?.app_logo_url;

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (window.matchMedia?.('(display-mode: standalone)').matches || (window.navigator as any).standalone) return;
    if (window.localStorage.getItem('findify-mtm-install-prompt-seen') === '1') return;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
  }, []);

  const dismiss = () => {
    window.localStorage.setItem('findify-mtm-install-prompt-seen', '1');
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return dismiss();
    installEvent.prompt();
    await installEvent.userChoice;
    dismiss();
  };

  return <Modal visible={visible} transparent animationType="fade" onRequestClose={dismiss}>
    <View style={styles.installBackdrop}>
      <View style={[styles.installCard, { backgroundColor: colors.background }]}>
        {museumLogo && <View style={[styles.installLogoContainer, { backgroundColor: colors.secondary }]}>
          <Image source={{ uri: museumLogo }} resizeMode="contain" accessibilityLabel="Museu da Terra de Miranda" style={styles.installLogo} />
        </View>}
        <PlaceText style={[styles.installTitle, { color: colors.primary }]}>Instale a APP do museu</PlaceText>
        <PlaceText style={[styles.installMessage, { color: colors.text }]}>Tenha acesso mais rápido à APP do Museu da Terra de Miranda, mesmo a partir do ecrã inicial.</PlaceText>
        <Pressable style={[styles.installButton, { backgroundColor: colors.primary }]} onPress={install}>
          <PlaceText style={styles.installButtonText}>Instalar app</PlaceText>
        </Pressable>
        <Pressable style={styles.installLaterButton} onPress={dismiss}>
          <PlaceText style={[styles.installLaterText, { color: colors.primary }]}>Agora não</PlaceText>
        </Pressable>
      </View>
    </View>
  </Modal>;
};

const WorkspaceWrapper = () => {
  const findifyDataContext = useContext(FindifyDataContext);

  if (findifyDataContext?.visitWorkspaceSelected === false && findifyDataContext?.hasWorkspaceError) {
    return (
      <View style={styles.centerContainer}>
        <PlaceText>Error loading workspace. Please check your connection.</PlaceText>
      </View>
    );
  }

  if (!findifyDataContext?.visitWorkspaceSelected) {
    return <FindifyLoadingScreen />;
  }

  const theme = findifyDataContext.workspace?.app_settings?.app_theme;
  const workspaceId = Number(findifyDataContext.workspace?.workspace_id ?? Config.WORKSPACE_ID);

  if (workspaceId === 193) {
    return <PlaceApp />;
  }

  switch (theme) {
    case 'museum':
      return <MuseumApp />;
    case 'events':
      return <EventsApp />;
    case 'default':
      return <PlaceApp />;
    default:
      return (
        <View style={styles.centerContainer}>
          <PlaceText>Coming soon: Theme {String(theme ?? 'undefined')}</PlaceText>
        </View>
      );
  }
};

const AppWithoutProviders = (): JSX.Element => {
  const findifyDataContext = useContext(FindifyDataContext);
  const navigationRef = useMemo(() => createNavigationContainerRef<any>(), []);
  console.log("AppWithoutProviders render:", {
    workspaceId: Config.WORKSPACE_ID,
    visitWorkspaceSelected: findifyDataContext?.visitWorkspaceSelected,
    isStorageLoading: findifyDataContext?.isStorageLoading
  });

  const loadConfiguredWorkplace = useCallback(async (context: FindifyDataContextType) => {
    const mobileWorkspacesApi = new MobileWorkspacesApi();

    const filters = {
      published_app_eq: 1,
      workspace_id: Config.WORKSPACE_ID
    };

    try {
      const data = await mobileWorkspacesApi.getList(0, 100, null, filters, null);
      if (data && data.results && data.results.length > 0) {
        context.setAvailableWorkspaces(data.results);
        context.setWorkspace(data.results[0]);
        context.setVisitWorkspaceSelected(true);
        context.setHasWorkspaceError(false);
      } else {
        context.setHasWorkspaceError(true);
      }
    } catch (error) {
      console.error("Failed to load configured workplace:", error);
      context.setHasWorkspaceError(true);
    }
  }, []);

  useEffect(() => {
    if (Config.WORKSPACE_ID && findifyDataContext && !findifyDataContext.isStorageLoading && !findifyDataContext.visitWorkspaceSelected) {
      loadConfiguredWorkplace(findifyDataContext);
    }
  }, [findifyDataContext?.isStorageLoading, findifyDataContext?.visitWorkspaceSelected, loadConfiguredWorkplace]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !findifyDataContext?.visitWorkspaceSelected || typeof window === 'undefined') return;

    const pushGuardState = () => {
      window.history.pushState({ findifyBackGuard: true }, document.title, window.location.href);
    };

    pushGuardState();

    const handleBrowserBack = () => {
      if (navigationRef.isReady() && navigationRef.canGoBack()) {
        navigationRef.goBack();
      }

      // Keep one browser-history entry ahead of the app so Android/browser back
      // does not close the installed PWA when the internal stack is at root.
      setTimeout(pushGuardState, 0);
    };

    window.addEventListener('popstate', handleBrowserBack);
    return () => window.removeEventListener('popstate', handleBrowserBack);
  }, [findifyDataContext?.visitWorkspaceSelected, navigationRef]);

  const mainNavigator = useMemo(() => {
    if (!findifyDataContext || findifyDataContext.isStorageLoading) {
      return <FindifyLoadingScreen />;
    }

    return (
      <NavigationContainer ref={navigationRef} documentTitle={{ enabled: false }}>
        <Stack.Navigator
          screenOptions={{
            animation: 'fade',
            animationTypeForReplace: 'pop',
            animationDuration: 100,
            headerShown: false
          }}
        >
          {!findifyDataContext.visitWorkspaceSelected && !Config.WORKSPACE_ID && (
            <Stack.Group>
              <Stack.Screen name="InitialPage" component={InitialPage} />
              <Stack.Screen name="InitialPagePlaceList" component={InitialPagePlacesList} />
              <Stack.Screen name="ScanQR" component={ScanQrTab} />
            </Stack.Group>
          )}
          {(findifyDataContext.visitWorkspaceSelected || Config.WORKSPACE_ID) && (
            <Stack.Group>
              <Stack.Screen name="WorkspacePage" component={WorkspaceWrapper} />
            </Stack.Group>
          )}
        </Stack.Navigator>
      </NavigationContainer>
    );
  }, [findifyDataContext?.isStorageLoading, findifyDataContext?.visitWorkspaceSelected]);

  return (
    <SafeAreaProvider>
      <GestureHandlerRootView style={styles.root}>
        {mainNavigator}
        <PwaInstallPrompt />
        <Toast config={FindifyToastConfig} />
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    display: 'flex',
  },
  installBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 30, 73, 0.52)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  installCard: {
    width: '100%',
    maxWidth: 390,
    borderRadius: 24,
    padding: 26,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  installLogoContainer: {
    maxWidth: '100%',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginBottom: 14,
  },
  installLogo: { width: 180, height: 60, maxWidth: '100%' },
  installTitle: { fontSize: 24, lineHeight: 29, fontWeight: '800', textAlign: 'center' },
  installMessage: { marginTop: 10, fontSize: 15, lineHeight: 22, textAlign: 'center' },
  installButton: { width: '100%', borderRadius: 14, paddingVertical: 14, marginTop: 22, alignItems: 'center' },
  installButtonText: { color: '#fff', fontSize: 15, fontWeight: '800' },
  installLaterButton: { paddingVertical: 12, paddingHorizontal: 18 },
  installLaterText: { fontSize: 14, fontWeight: '700' },
});

export default AppWithoutProviders;
